import { makeAutoObservable, runInAction } from "mobx";
import { extractThreadId } from "@/utils/unifiedEmail";
import { mergeContactPanelAttributes } from "./campaignConversationCore/contactMerge";
import {
  deleteMessagesRequest,
  markUnifiedMessageRead,
  putMessageRequest,
  searchContactByEmail,
} from "./campaignConversationCore/conversationViewerApi";
import {
  getConversationTab,
  getMostRecentContactMessage,
  getThreadId,
  processViewerMessages,
} from "./campaignConversationCore/threadUtils";

/**
 * ViewModel for unified / campaign conversation viewer (contact fetch, read receipts, mutations, intents).
 */
class ConversationViewerStore {
  campaign = null;
  account = "";
  navigationSource = "unified-inbox";
  initialConversation = null;

  selectedThread = null;
  initialTab = 0;
  showContactDetailsPanel = false;
  fetchedContactData = null;
  /** True while `searchContactByEmail` is in flight for the current thread email. */
  contactPanelFetchLoading = false;

  pendingCacheUpdates = [];

  newReplyCollection = {};

  intents = [];
  messageIntents = {};

  approvalDialog = { open: false, messageId: null };
  deleteDialog = { open: false, message: null, isLoading: false };
  editDialog = { open: false, message: null, isLoading: false };
  replyDialogOpen = false;
  replyDialogLoading = false;

  selectedMessage = null;

  _sessionCampaignId = null;
  /** @type {string|null} Email used for stale-guard on contact fetch */
  _contactFetchTargetEmail = null;
  /** Monotonic id so overlapping `syncContactFetch` runs do not clobber loading or results. */
  _contactFetchSeq = 0;
  /** @type {string|null} Conversation key whose contact payload is cached in `fetchedContactData`. */
  _contactFetchThreadKey = null;

  /** @type {(() => void)|null} */
  _onClose = null;
  /** @type {((to: string|number, opts?: object) => void)|null} */
  _navigate = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** @returns {Record<string, unknown>} */
  get contactPanelAttributes() {
    return mergeContactPanelAttributes(
      this.selectedThread?.messages?.[0]?.metadata?.contact_vars,
      this.fetchedContactData
    );
  }

  /** @returns {string} */
  get selectedThreadEmail() {
    const thread = this.selectedThread;
    if (!thread) return "";
    return (
      thread.email ||
      thread.contact_email ||
      thread.messages?.find((message) => message.role === "CONTACT")?.sender ||
      thread.messages?.[0]?.sender ||
      ""
    );
  }

  /**
   * True while the Contact Information panel should show the loading skeleton for the current thread.
   * Requires an in-flight or just-started contact lookup when the panel is open (including re-fetch on open).
   */
  get contactDetailsPanelLoading() {
    if (!this.showContactDetailsPanel) return false;
    if (!this.selectedThreadEmail || !this.account) return false;
    return this.contactPanelFetchLoading;
  }

  /** @returns {string} */
  get selectedThreadCacheKey() {
    return getThreadId(this.selectedThread);
  }

  isInitialConversation(conversation) {
    return Boolean(this.initialConversation && conversation?.id === this.initialConversation.id);
  }

  reset() {
    this._sessionCampaignId = null;
    this._contactFetchTargetEmail = null;
    this._contactFetchThreadKey = null;
    this.campaign = null;
    this.account = "";
    this.navigationSource = "unified-inbox";
    this.initialConversation = null;
    this.selectedThread = null;
    this.initialTab = 0;
    this.showContactDetailsPanel = false;
    this.fetchedContactData = null;
    this.contactPanelFetchLoading = false;
    this._contactFetchSeq = 0;
    this.pendingCacheUpdates = [];
    this.newReplyCollection = {};
    this.intents = [];
    this.messageIntents = {};
    this.approvalDialog = { open: false, messageId: null };
    this.deleteDialog = { open: false, message: null, isLoading: false };
    this.editDialog = { open: false, message: null, isLoading: false };
    this.replyDialogOpen = false;
    this.replyDialogLoading = false;
    this.selectedMessage = null;
  }

  resetForNewCustomer() {
    this.reset();
  }

  /** @param {((to: string|number, opts?: object) => void)|null|undefined} navigate */
  setNavigate(navigate) {
    this._navigate = navigate ?? null;
  }

  /**
   * @param {(() => void)|null|undefined} onClose
   */
  setOnClose(onClose) {
    this._onClose = onClose ?? null;
  }

  /**
   * @param {{
   *   campaign: object|null,
   *   account: string,
   *   navigationSource?: string,
   *   onClose?: (() => void)|null,
   *   initialConversation?: object|null,
   * }} params
   */
  setSessionContext({ campaign, account, navigationSource = "unified-inbox", onClose, initialConversation }) {
    const nextId = campaign?.id ?? null;
    const prevId = this._sessionCampaignId;

    if (prevId != null && nextId != null && prevId !== nextId) {
      this.reset();
    }

    runInAction(() => {
      this._sessionCampaignId = nextId;
      this.campaign = campaign;
      this.account = account ?? "";
      this.navigationSource = navigationSource;
      if (onClose !== undefined) this._onClose = onClose ?? null;
      if (initialConversation !== undefined) this.initialConversation = initialConversation ?? null;
    });
  }

  closeViewer() {
    const safeSource = ["campaigns-dashboard", "unified-inbox"].includes(this.navigationSource)
      ? this.navigationSource
      : "unified-inbox";
    if (safeSource === "campaigns-dashboard") {
      this._onClose?.();
      return;
    }
    this._onClose?.();
    if (this.pendingCacheUpdates.length > 0) {
      const cacheUpdates = this.pendingCacheUpdates.map((update) => ({
        messageId: update?.messageId ?? null,
        campaignId: update?.campaignId ?? null,
        sender: update?.sender ?? null,
        receiver: update?.receiver ?? null,
        updateType: update?.updateType ?? null,
        newValue: update?.newValue,
        timestamp: update?.timestamp ?? null,
        customer: update?.customer ?? null,
      }));
      this._navigate?.("/unified-email-inbox", {
        state: { cacheUpdates, customer: this.account },
      });
      return;
    }
    const historyIndex = window.history?.state?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) {
      this._navigate?.(-1);
      return;
    }
    this._navigate?.("/unified-email-inbox");
  }

  seedPendingCacheFromInitialConversation() {
    if (!this.initialConversation?.messages || !this.campaign) return;
    const contactMsg = getMostRecentContactMessage(this.initialConversation.messages);
    if (!contactMsg) return;
    runInAction(() => {
      this.pendingCacheUpdates = [
        {
          messageId: contactMsg.id,
          campaignId: this.campaign.id,
          sender: contactMsg.sender,
          receiver: contactMsg.receiver,
          updateType: "read_status",
          newValue: true,
          timestamp: Date.now(),
          customer: this.account,
        },
      ];
    });
  }

  bootstrapInitialConversationIfNeeded() {
    if (!this.initialConversation || this.selectedThread) return;
    const sourceMessages =
      this.initialConversation.data?.messages || this.initialConversation.allMessages || [];
    const contactEmail =
      this.initialConversation.contact_email ||
      this.initialConversation.data?.messages?.[0]?.sender ||
      sourceMessages[0]?.sender;

    const processedThread = {
      ...this.initialConversation,
      name: contactEmail || "Unknown Contact",
      messages: processViewerMessages(sourceMessages),
      contact_email: contactEmail,
      email: contactEmail,
    };

    runInAction(() => {
      this.selectedThread = processedThread;
      this.initialTab = getConversationTab(processedThread, { campaign: this.campaign });
      this.fetchedContactData = null;
      this.contactPanelFetchLoading = false;
      this._contactFetchTargetEmail = null;
      this._contactFetchThreadKey = null;
      this._contactFetchSeq = 0;
    });
    this._syncIntentsFromThread(processedThread);
    void this.handleMessageRead(processedThread);
  }

  selectThread(thread) {
    if (!thread) return;

    const threadId = thread.id || thread.contact_email || thread.email;
    const messages = processViewerMessages(thread.allMessages);
    const newReplies = (this.newReplyCollection[threadId] || []).filter((r) => r.status === "sent");
    const fallbackEmail = thread.contact_email || thread.email || messages.find((message) => message.role === "CONTACT")?.sender;

    const processedThread = {
      ...thread,
      name: fallbackEmail,
      email: fallbackEmail || thread.email || "",
      contact_email: thread.contact_email || fallbackEmail || "",
      messages: [...messages, ...newReplies],
    };

    runInAction(() => {
      this.selectedThread = processedThread;
      this.initialTab = getConversationTab(processedThread, { campaign: this.campaign });
      this.showContactDetailsPanel = false;
      this.fetchedContactData = null;
      this.contactPanelFetchLoading = false;
      this._contactFetchTargetEmail = null;
      this._contactFetchThreadKey = null;
      this._contactFetchSeq = 0;
    });
    this._syncIntentsFromThread(processedThread);
    void this.handleMessageRead(processedThread);
  }

  handleBackClick() {
    runInAction(() => {
      this.selectedMessage = null;
      this.selectedThread = null;
      this.showContactDetailsPanel = false;
      this.fetchedContactData = null;
      this.contactPanelFetchLoading = false;
      this._contactFetchTargetEmail = null;
      this._contactFetchThreadKey = null;
      this._contactFetchSeq = 0;
    });
  }

  openContactPanel() {
    runInAction(() => {
      this.showContactDetailsPanel = true;
    });
    void this.syncContactFetch();
  }

  closeContactPanel() {
    this.showContactDetailsPanel = false;
  }

  syncSelectedThreadTab() {
    if (this.selectedThread) {
      this.initialTab = getConversationTab(this.selectedThread, { campaign: this.campaign });
    }
  }

  /**
   * Contact search when thread email changes (call from view useEffect).
   */
  async syncContactFetch() {
    const email = this.selectedThreadEmail;
    const threadKey = this.selectedThreadCacheKey;
    if (!email || !this.account) {
      runInAction(() => {
        this.fetchedContactData = null;
        this._contactFetchTargetEmail = null;
        this._contactFetchThreadKey = null;
        this.contactPanelFetchLoading = false;
      });
      return;
    }

    const hasCachedContactForThread =
      this._contactFetchThreadKey === threadKey &&
      this._contactFetchTargetEmail === email &&
      this.fetchedContactData != null;
    if (hasCachedContactForThread) return;

    const hasInFlightForThread =
      this.contactPanelFetchLoading &&
      this._contactFetchThreadKey === threadKey &&
      this._contactFetchTargetEmail === email;
    if (hasInFlightForThread) return;

    const emailToFetch = email;
    const emailChanged = this._contactFetchTargetEmail !== emailToFetch;
    let seq;
    runInAction(() => {
      this._contactFetchSeq += 1;
      seq = this._contactFetchSeq;
      if (this._contactFetchThreadKey !== threadKey || emailChanged) {
        this.fetchedContactData = null;
      }
      this._contactFetchTargetEmail = emailToFetch;
      this._contactFetchThreadKey = threadKey;
      this.contactPanelFetchLoading = true;
    });

    try {
      const contact = await searchContactByEmail({
        customer: this.account,
        email: emailToFetch,
      });

      runInAction(() => {
        if (
          this._contactFetchTargetEmail === emailToFetch &&
          this._contactFetchThreadKey === threadKey &&
          this._contactFetchSeq === seq
        ) {
          this.fetchedContactData = contact ?? null;
        }
      });
    } catch (error) {
      console.error("[ContactFetch] Failed to fetch contact:", error);
    } finally {
      runInAction(() => {
        if (
          this._contactFetchTargetEmail === emailToFetch &&
          this._contactFetchThreadKey === threadKey &&
          this._contactFetchSeq === seq
        ) {
          this.contactPanelFetchLoading = false;
        }
      });
    }
  }

  async handleMessageRead(conversation) {
    if (!conversation?.messages || !this.campaign) return;
    const contactMsg = getMostRecentContactMessage(conversation.messages);
    if (!contactMsg) return;

    if (!this.isInitialConversation(conversation)) {
      try {
        const thread = extractThreadId(contactMsg?.real_message, conversation);
        const tsRaw =
          contactMsg?.real_message?.timestamp ||
          contactMsg?.real_message?.insert_date ||
          contactMsg?.timestamp ||
          contactMsg?.insert_date;
        const timestamp =
          typeof tsRaw === "string" ? tsRaw : tsRaw ? new Date(tsRaw).toISOString() : undefined;

        if (!thread || !timestamp) {
          throw new Error(
            `[ConversationViewer] Missing thread/timestamp for markUnifiedMessage (thread=${String(
              thread
            )}, timestamp=${String(timestamp)})`
          );
        }

        await markUnifiedMessageRead({
          customer: this.account,
          campaignId: this.campaign.id,
          thread,
          timestamp,
        });
      } catch (error) {
        console.error("[ConversationViewer] Failed to mark message as read:", error);
        return;
      }
    }

    const cacheUpdate = {
      messageId: contactMsg.id,
      campaignId: this.campaign.id,
      sender: contactMsg.sender,
      receiver: contactMsg.receiver,
      updateType: "read_status",
      newValue: true,
      timestamp: Date.now(),
      customer: this.account,
    };

    runInAction(() => {
      const prev = this.pendingCacheUpdates;
      const idx = prev.findIndex((u) => u.messageId === cacheUpdate.messageId);
      this.pendingCacheUpdates =
        idx >= 0 ? [...prev.slice(0, idx), cacheUpdate, ...prev.slice(idx + 1)] : [...prev, cacheUpdate];
    });
  }

  _syncIntentsFromThread(thread) {
    if (!thread?.messages) return;
    runInAction(() => {
      const nextMessageIntents = { ...this.messageIntents };
      const nextIntents = [...this.intents];

      thread.messages
        .filter((m) => m.type === "contact")
        .forEach((message) => {
          const detections = message.metadata?.intent_detection?.intents || [];
          detections.forEach((intentName) => {
            if (!nextMessageIntents[message.id]?.some((i) => i.text === intentName)) {
              nextMessageIntents[message.id] = [...(nextMessageIntents[message.id] || []), { text: intentName }];
            }
            if (!nextIntents.some((i) => i.intent_name === intentName)) {
              const protectedIntent = thread.campaign?.protected_intents?.find((pi) => pi.intent_name === intentName);
              nextIntents.push(
                protectedIntent || {
                  id: intentName,
                  intent_name: intentName,
                  description: "",
                  context: [],
                  examples: [],
                  responses: [],
                }
              );
            }
          });
        });

      this.messageIntents = nextMessageIntents;
      this.intents = nextIntents;
    });
  }

  openReplyDialog() {
    this.replyDialogOpen = true;
    this.replyDialogLoading = false;
  }

  closeReplyDialog() {
    this.replyDialogOpen = false;
    this.replyDialogLoading = false;
  }

  openEditDialog(message) {
    this.editDialog = { open: true, message, isLoading: false };
  }

  closeEditDialog() {
    this.editDialog = { open: false, message: null, isLoading: false };
  }

  openDeleteDialog(message) {
    this.deleteDialog = { open: true, message, isLoading: false };
  }

  closeDeleteDialog() {
    this.deleteDialog = { open: false, message: null, isLoading: false };
  }

  openApprovalDialog(messageId) {
    this.approvalDialog = { open: true, messageId };
  }

  closeApprovalDialog() {
    this.approvalDialog = { open: false, messageId: null };
  }

  /**
   * @param {string} content
   * @returns {Promise<{ ok: boolean, error?: string, successMessage?: string }>}
   */
  async saveReply(content) {
    if (!this.campaign) return { ok: false, error: "No campaign" };
    try {
      runInAction(() => {
        this.replyDialogLoading = true;
      });
      if (!content.trim()) throw new Error("Reply content cannot be empty");

      const messages = this.selectedThread?.messages || [];
      const referenceMessage =
        messages.find((m) => ["INITIAL_SMS_SENT", "INITIAL_EMAIL_SENT"].includes(m.role?.toUpperCase())) ||
        messages[0];
      if (!referenceMessage) throw new Error("No valid message found in conversation.");

      const putRes = await putMessageRequest({
        message_id: referenceMessage.id,
        campaign_id: this.campaign.id,
        body: content,
        check_gramma: true,
        new_reply: true,
      });

      const { body: bodyStr, statusCode } = putRes || {};
      if (!bodyStr) throw new Error("Invalid response format from server");

      const body = JSON.parse(bodyStr);
      if (statusCode !== "200") throw new Error(body.message || "Failed to send reply");

      const newMessage = {
        id: `reply-${Date.now()}`,
        type: "agent",
        role: "AGENT",
        content,
        timestampStr: new Date().toLocaleString(),
        status: "sent",
        real_message: {
          id: referenceMessage.id,
          content,
          role: "AGENT",
          status: "sent",
        },
      };

      const threadId = getThreadId(this.selectedThread);
      runInAction(() => {
        this.newReplyCollection = {
          ...this.newReplyCollection,
          [threadId]: [...(this.newReplyCollection[threadId] || []), newMessage],
        };
        if (this.selectedThread) {
          this.selectedThread = { ...this.selectedThread, messages: [...this.selectedThread.messages, newMessage] };
        }
        this.replyDialogOpen = false;
        this.replyDialogLoading = false;
      });

      return { ok: true, successMessage: body.message || "Reply sent successfully" };
    } catch (error) {
      console.error("Error sending reply:", error);
      runInAction(() => {
        this.replyDialogLoading = false;
      });
      return { ok: false, error: error.message || "Failed to send reply" };
    }
  }

  /**
   * @param {string} content
   * @returns {Promise<{ ok: boolean, error?: string, successMessage?: string }>}
   */
  async saveEdit(content) {
    const msg = this.editDialog.message;
    if (!this.campaign || !msg) return { ok: false, error: "Invalid state" };
    try {
      runInAction(() => {
        this.editDialog = { ...this.editDialog, isLoading: true };
      });

      const putRes = await putMessageRequest({
        message_id: msg.id,
        campaign_id: this.campaign.id,
        body: content,
        check_gramma: true,
        new_reply: false,
      });

      const { body: bodyStr, statusCode } = putRes || {};
      if (!bodyStr) throw new Error("Invalid response format from server");

      const body = JSON.parse(bodyStr);
      if (statusCode !== "200") throw new Error(body.message || "Failed to update message");

      const updateMsg = (m) =>
        m.id === msg.id ? { ...m, content: body.data.body, message: body.message } : m;

      const threadId = getThreadId(this.selectedThread);
      runInAction(() => {
        if (this.selectedThread) {
          this.selectedThread = { ...this.selectedThread, messages: this.selectedThread.messages.map(updateMsg) };
        }
        if (this.newReplyCollection[threadId]) {
          this.newReplyCollection = {
            ...this.newReplyCollection,
            [threadId]: this.newReplyCollection[threadId].map(updateMsg),
          };
        }
        this.editDialog = { open: false, message: null, isLoading: false };
      });

      return { ok: true, successMessage: body.message || "Message updated successfully" };
    } catch (error) {
      console.error("Error updating message:", error);
      runInAction(() => {
        this.editDialog = { ...this.editDialog, isLoading: false };
      });
      return { ok: false, error: error.message || "Failed to update message" };
    }
  }

  /**
   * @param {Record<string, unknown>} message
   * @returns {Promise<{ ok: boolean, error?: string }>}
   */
  async deleteMessage(message) {
    try {
      runInAction(() => {
        this.deleteDialog = { ...this.deleteDialog, isLoading: true };
      });
      await deleteMessagesRequest(message);
      runInAction(() => {
        if (this.selectedThread) {
          this.selectedThread = {
            ...this.selectedThread,
            messages: this.selectedThread.messages.filter((m) => m.id !== message.id),
          };
        }
        this.deleteDialog = { open: false, message: null, isLoading: false };
      });
      return { ok: true };
    } catch (error) {
      console.error("Error deleting message:", error);
      runInAction(() => {
        this.deleteDialog = { open: false, message: null, isLoading: false };
      });
      return { ok: false, error: "Error deleting message. Please try again later." };
    }
  }

  /**
   * @param {string} messageId
   */
  handleApproveSuccess(messageId) {
    const updateStatus = (msg) => (msg.id === messageId ? { ...msg, status: "approved" } : msg);

    runInAction(() => {
      if (this.selectedThread) {
        this.selectedThread = { ...this.selectedThread, messages: this.selectedThread.messages.map(updateStatus) };
      }
      const threadId = getThreadId(this.selectedThread);
      if (this.newReplyCollection[threadId]) {
        this.newReplyCollection = {
          ...this.newReplyCollection,
          [threadId]: this.newReplyCollection[threadId].map(updateStatus),
        };
      }
    });

    if (this.campaign && this.selectedThread) {
      const approvedMessage = this.selectedThread.messages.find((msg) => msg.id === messageId);
      if (!approvedMessage) {
        console.error("[ERROR] Approved message not found:", messageId);
        return { ok: false };
      }

      const contactMsg = getMostRecentContactMessage(this.selectedThread.messages);
      if (contactMsg) {
        const cacheRemovalUpdate = {
          messageId: contactMsg.id,
          campaignId: this.campaign.id,
          sender: contactMsg.sender || contactMsg.real_message?.sender,
          receiver: contactMsg.receiver || contactMsg.real_message?.receiver,
          updateType: "conversation_removal",
          timestamp: Date.now(),
          customer: this.account,
        };
        runInAction(() => {
          const prev = this.pendingCacheUpdates;
          const idx = prev.findIndex((u) => u.messageId === cacheRemovalUpdate.messageId);
          this.pendingCacheUpdates =
            idx >= 0
              ? [...prev.slice(0, idx), cacheRemovalUpdate, ...prev.slice(idx + 1)]
              : [...prev, cacheRemovalUpdate];
        });
      }
    }
    return { ok: true };
  }

  cleanupProcessedRepliesForThread(threadId) {
    runInAction(() => {
      this.newReplyCollection = {
        ...this.newReplyCollection,
        [threadId]: (this.newReplyCollection[threadId] || []).filter((r) =>
          ["pending_send", "pending_approval", "draft"].includes(r.status)
        ),
      };
    });
  }

  setSelectedMessage(message) {
    this.selectedMessage = message;
  }

  /**
   * Local tag apply (matches prior component behavior: always append `intent` list; dedupe per-message tags).
   * @param {string} messageId
   * @param {string} text
   * @param {{ intent_name: string }} intent
   */
  applyTagToMessage(messageId, text, intent) {
    runInAction(() => {
      this.intents = [...this.intents, intent];
      if (this.messageIntents[messageId]?.some((i) => i.text === intent.intent_name)) return;
      this.messageIntents = {
        ...this.messageIntents,
        [messageId]: [...(this.messageIntents[messageId] || []), { text }],
      };
    });
  }

  removeMessageIntentTag(messageId, text) {
    runInAction(() => {
      this.messageIntents = {
        ...this.messageIntents,
        [messageId]: (this.messageIntents[messageId] || []).filter((t) => t.text !== text),
      };
    });
  }
}

export default ConversationViewerStore;
